import * as Sentry from '@sentry/react';

/**
 * Sentry for the landing (marketing site + signup funnel).
 *
 * Mirrors the API's convention (`docs/operations/env-vars.md`): **an empty DSN
 * disables it**. That is what lets this ship before the Sentry project exists —
 * `Sentry.init` is simply never called, no network, no cost, nothing to undo.
 * Turning it on later is one build variable, not a code change.
 *
 * "No cost" is literal, and worth not optimising away: Vite inlines
 * `import.meta.env.VITE_SENTRY_DSN` at build time, so with no DSN the early
 * return below becomes unconditional, everything after it is dead code, and
 * Rollup drops the whole `@sentry/react` import. Measured: **0 bytes** without a
 * DSN, +28 KB gzipped with one. Read the DSN from a runtime source instead and
 * that property disappears — the SDK would ship on every page whether or not
 * anyone is collecting.
 *
 * Two things about this app in particular:
 *
 * 1. **It is prerendered.** `vite-react-ssg build` executes the app in Node to
 *    emit ~488 static pages. Initialising there would install browser
 *    instrumentation in a build process and, worse, report build-time noise as
 *    if it were user-facing. So this is browser-only, matching the
 *    `typeof window` guard `main.tsx` already uses for its chunk-reload latch.
 *
 * 2. **The funnel handles personal data** — name, email, company, VAT. So
 *    `sendDefaultPii` stays false and the default integrations that scrape
 *    inputs are not added. Errors, not surveillance.
 *
 * Tracing is off by default (sample rate 0), same as the API: this exists to
 * catch breakage, and a marketing site with hundreds of pages would otherwise
 * bill for volume nobody reads.
 *
 * **Why this is not behind the cookie gate** (DIT-402, ADR-0035). The consent
 * layer implements ePrivacy art. 5(3) — storing or reading information on the
 * visitor's device. This SDK does neither: no cookies, no localStorage, state
 * kept in memory for the page's lifetime. What it does do is send an error
 * payload to a processor, which is a GDPR lawful-basis question (service
 * integrity) belonging in the privacy policy, not a consent category. Gating it
 * would also lose the reports from exactly the visitors who declined — the ones
 * whose session is most likely to have broken.
 *
 * That reasoning holds only while this stays error-only. **Turning on tracing or
 * Session Replay changes the answer**: both are behavioural, replay reads the
 * page continuously, and either belongs behind the `analytics` category via
 * `hasConsent('analytics')` from `@shared/consent`. Raising
 * `VITE_SENTRY_TRACES_SAMPLE_RATE` above 0 is therefore not a config tweak — it
 * is a consent decision, and ADR-0035 has to be revisited with it.
 */
export function initSentry(): void {
  if (typeof window === 'undefined') return; // prerender pass — see above

  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) return; // unset ⇒ disabled, deliberately

  Sentry.init({
    dsn,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || 'unknown',
    release: import.meta.env.VITE_SENTRY_RELEASE || undefined,
    sendDefaultPii: false,
    tracesSampleRate: Number(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE ?? 0),
    // A stale-chunk import failure is already handled in main.tsx with a single
    // hard reload; it is a deploy artefact, not a fault worth alerting on.
    ignoreErrors: [
      'Failed to fetch dynamically imported module',
      'Importing a module script failed',
    ],
  });
}
